import * as React from 'react';
import { useCurrentPage } from './context';
import { resolveSyncIfLoadedOrThrow } from '../resolve-component';
import { createErrorBoundary, type ErrorProps } from '../error-boundary';
import { currentPageLoadFailure, isPageError, currentPage } from './state';
import { isComponentType } from '../register-component';
import { URLX } from '../url';

type PropType = {
  page: Excite.ContentApi.IComponentData & { keepComponents?: boolean };
};

export function Page(props: PropType) {
  const pathname = new URLX(props.page.url).pathname;
  const keyRef = React.useRef<string>(pathname);

  if (!props.page.keepComponents) {
    keyRef.current = pathname;
  }

  const Component = resolveSyncIfLoadedOrThrow(props.page.componentName);
  return <Component key={keyRef.current} {...props.page} />;
}

export function CurrentPage() {
  const currentPage = useCurrentPage();
  const page = isPageError(currentPage) ? currentPage?.errorPage : currentPage;
  if (page) {
    return <Page page={page} />;
  }

  throw new Error('Unknown current page');
}

export function createCurrentPageWithErrorBoundary(Fallback?: React.ComponentType<ErrorProps>) {
  return createErrorBoundary(CurrentPage, Fallback, [currentPage, currentPageLoadFailure]);
}

export function getCurrentPage() {
  return currentPage.currentSync();
}

export function useIsCurrentPageType<TPage>(type?: string) {
  const [isCurrentPageType, setIsCurrentPageType] = React.useState(() => {
    const page = getCurrentPage();
    return isComponentType(page, type);
  });

  React.useEffect(() => {
    const unsubscribe = currentPage.on(data => {
      const nextIsCurrentPageType = isComponentType(data, type);
      if (isCurrentPageType !== nextIsCurrentPageType) {
        setIsCurrentPageType(nextIsCurrentPageType);
      }
    });

    return unsubscribe;
  }, [isCurrentPageType, type]);

  return isCurrentPageType;
}
